import Dexie from 'dexie';
import { storageName } from '../utils/storage';
import { indexedDbOperation } from '../utils/indexedDbOperation';
import { validDrawingId, validPaths, validCrop, FULL_CROP } from './model';

const database = new Dexie(storageName('WiwDrawingDrafts'));
database.version(1).stores({ drafts: 'key, scope, updatedAt' });
const memory = new Map();
const writes = new Map();
const WEEK = 7 * 24 * 60 * 60 * 1000;
export const drawingScope = (userId, storyId, draftId) => `${userId}:${storyId}:${draftId}`;
const keyOf = (scope, id) => `${scope}:${id}`;
const validRow = row => row && validDrawingId(row.id) && validPaths(row.paths)
  && (!row.savedPaths || validPaths(row.savedPaths)) && (!row.crop || validCrop(row.crop))
  && (!row.savedCrop || validCrop(row.savedCrop)) && row.updatedAt > Date.now() - WEEK;

export async function readDrawing(scope, id) {
  if (!validDrawingId(id)) return null;
  const key = keyOf(scope, id);
  const cached = memory.get(key);
  if (cached && validRow(cached)) return cached;
  const row = await database.drafts.get(key);
  if (row?.scope !== scope || row.id !== id || !validRow(row)) return null;
  memory.set(key, row);
  return row;
}

export async function pendingDrawing(scope) {
  // Cleanup is local only and never touches the comment's exported image.
  let stored = [];
  let storageError;
  try {
    await indexedDbOperation(database, 'rw', ['drafts'], () => database.drafts.where('updatedAt').below(Date.now() - WEEK).delete());
    stored = await indexedDbOperation(database, 'r', ['drafts'], () => database.drafts.where('scope').equals(scope).toArray());
  } catch (error) { storageError = error; }
  for (const [key, row] of memory) if (!validRow(row) && !writes.has(key)) memory.delete(key);
  const rows = new Map(stored.map(row => [row.key, row]));
  for (const [key, row] of memory) if (row.scope === scope) rows.set(key, row);
  const pending = [...rows.values()].filter(row => row.scope === scope && !row.savedPaths && validRow(row))
    .sort((a, b) => b.updatedAt - a.updatedAt)[0] || null;
  if (pending) memory.set(pending.key, pending);
  // A blocked/evicted database must not hide the working copy already held by
  // this tab. Still signal storage failure when there is nothing to recover.
  if (!pending && storageError) throw storageError;
  return pending;
}

export function writeDrawing(scope, id, paths, { commit = false, crop = FULL_CROP } = {}) {
  if (!validDrawingId(id) || !validPaths(paths) || !validCrop(crop)) return Promise.reject(new Error('DRAWING_LIMIT'));
  const key = keyOf(scope, id);
  const row = { key, scope, id, paths, crop, savedPaths: commit ? paths : memory.get(key)?.savedPaths,
    savedCrop: commit ? crop : memory.get(key)?.savedCrop, updatedAt: Date.now() };
  memory.set(key, row);
  // Serialize writes so a slow auto-save cannot overwrite a newer commit.
  const write = (writes.get(key) || Promise.resolve()).catch(() => {})
    .then(() => indexedDbOperation(database, 'rw', ['drafts'], () => database.drafts.put(row)));
  writes.set(key, write);
  return write.finally(() => { if (writes.get(key) === write) writes.delete(key); });
}

export async function discardDrawing(scope, id) {
  const key = keyOf(scope, id);
  await writes.get(key)?.catch(() => {});
  const row = memory.get(key);
  if (row?.savedPaths) return writeDrawing(scope, id, row.savedPaths, { commit: true, crop: row.savedCrop || FULL_CROP });
  memory.delete(key);
  await indexedDbOperation(database, 'rw', ['drafts'], () => database.drafts.delete(key));
}

export async function clearDrawings(scope) {
  const keys = [...memory].filter(([, row]) => row.scope === scope).map(([key]) => key);
  await Promise.all(keys.map(key => writes.get(key)?.catch(() => {})));
  keys.forEach(key => memory.delete(key));
  await indexedDbOperation(database, 'rw', ['drafts'], () => database.drafts.where('scope').equals(scope).delete());
}
